import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, useTheme } from '@mui/material';
import { LuCircleCheck, LuCircleHelp, LuInfo, LuTriangleAlert, LuX } from 'react-icons/lu';
import { ConfirmDialogPattern } from './ConfirmDialogPattern';
import { ConfirmDialogTransition } from './ConfirmDialogTransition';
/** Header padding and icon size; the pattern is centred on the icon from these. */
const HEADER_PADDING = 20;
const ICON_SIZE = 40;
const ICON_CENTER = {
  x: HEADER_PADDING + ICON_SIZE / 2,
  y: HEADER_PADDING + ICON_SIZE / 2
};

/** A sensible icon for the dialog's intent: a warning for destructive or risky colors, a check for success, else a question. */
function defaultIcon(variant, color) {
  if (variant === 'info') return LuInfo;
  if (color === 'error' || color === 'warning') return LuTriangleAlert;
  if (color === 'success') return LuCircleCheck;
  return LuCircleHelp;
}
export const ConfirmDialog = ({
  open,
  variant = 'confirm',
  title = 'Confirm Action',
  content = 'Are you sure you want to perform this action?',
  onConfirm,
  onCancel,
  onClickOutside,
  pendingAction = null,
  onExited,
  color = 'brand',
  confirmButtonText = 'Confirm',
  cancelButtonText,
  maxWidth = 'xs',
  icon,
  pattern = 'none'
}) => {
  const theme = useTheme();
  const isInfo = variant === 'info';
  const wantsIcon = icon === true || icon == null && pattern !== 'none';
  const Icon = wantsIcon ? defaultIcon(variant, color) : typeof icon === 'function' ? icon : null;
  // The icon and pattern take the confirm button's color; non-palette values ('inherit') fall back to brand.
  const palette = theme.palette;
  const tone = color && palette[color]?.main || palette.brand?.main || theme.palette.primary.main;
  const dismissLabel = cancelButtonText ?? (isInfo ? 'Close' : 'Cancel');
  const busy = !!pendingAction;
  const dismissWithoutCancelButton = () => {
    if (onClickOutside) {
      onClickOutside();
    } else {
      onCancel?.();
    }
  };
  const renderedContent = typeof content === 'function' ? content({
    close: dismissWithoutCancelButton
  }) : content;
  return <Dialog open={open} onClose={(_, reason) => {
    if (busy) return;
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      dismissWithoutCancelButton();
    } else {
      onCancel?.();
    }
  }} disableEscapeKeyDown={busy} maxWidth={maxWidth} fullWidth slots={{
    transition: ConfirmDialogTransition
  }} slotProps={{
    transition: {
      appear: true,
      onExited
    },
    paper: {
      // MUI focuses the paper on open; a dialog opened before any click/tap (e.g. on page load) would otherwise get the browser's focus-visible ring.
      sx: {
        borderRadius: '8px',
        outline: 'none'
      },
      style: {
        '--confirm-tone': tone
      }
    }
  }}>
            {Icon && <ConfirmDialogPattern variant={pattern} iconCenter={ICON_CENTER} />}

            {/* Top-right of the paper, above the icon and pattern; the title lines up under the icon instead. */}
            <IconButton size="small" onClick={dismissWithoutCancelButton} className="absolute! top-3.5! right-3.5! z-2" disabled={busy} aria-label="Close dialog">
                <LuX className="text-base" />
            </IconButton>

            {Icon && <div className="relative z-1" style={{
      paddingLeft: HEADER_PADDING,
      paddingTop: HEADER_PADDING
    }}>
                    <span className="flex items-center justify-center rounded-full text-xl text-(--confirm-tone) bg-[color-mix(in_srgb,var(--confirm-tone)_12%,var(--color-page))] ring-6 ring-[color-mix(in_srgb,var(--confirm-tone)_5%,transparent)]" style={{
        width: ICON_SIZE,
        height: ICON_SIZE
      }} data-testid="confirm-dialog-icon">
                        <Icon />
                    </span>
                </div>}

            <DialogTitle className={Icon ? 'relative z-1 px-5! pt-4! pr-12!' : 'relative z-1 pr-12!'}>{title}</DialogTitle>
            <DialogContent className={Icon ? 'relative z-1 -mt-2! -mb-1! px-5!' : 'relative z-1 -mt-2! -mb-1!'}>
                <div>{renderedContent}</div>
            </DialogContent>
            <DialogActions className="px-5! pb-5! gap-2.5!">
                {isInfo ? <Button variant="contained" color="neutral" disableElevation fullWidth onClick={onCancel} disabled={busy} startIcon={pendingAction === 'cancel' ? <CircularProgress color="inherit" size={18} /> : undefined}>
                        {dismissLabel}
                    </Button> : <>
                        <Button variant="contained" color="neutral" disableElevation fullWidth onClick={onCancel} disabled={busy} startIcon={pendingAction === 'cancel' ? <CircularProgress color="inherit" size={18} /> : undefined}>
                            {dismissLabel}
                        </Button>
                        <Button variant="contained" color={color} disableElevation fullWidth onClick={onConfirm} disabled={busy} loading={pendingAction === 'confirm'} loadingPosition="center">
                            {confirmButtonText}
                        </Button>
                    </>}
            </DialogActions>
        </Dialog>;
};